import flatLayPreview from "@/assets/preview-flat-lay.webp";
const FASHION_REELS_CDN = "https://cdn.pixro.ai/a9899ba8-1027-4449-ab7b-82cd06847194.mp4";
import ghostMannequin from "@/assets/preview-ghost-mannequin.webp";
import productShowcase from "@/assets/preview-product-showcase.webp";
import upscalePreview from "@/assets/preview-upscale.webp";
import virtualTryon from "@/assets/preview-virtual-tryon.webp";
import LazyVideo from "@/components/LazyVideo";
import OptimizedImage from "@/components/OptimizedImage";
import Reveal from "@/components/Reveal";
import { toolAppHref } from "@/lib/tools";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useRef } from "react";
import { Link } from "react-router-dom";

export type Feature = {
    title: string;
    desc: string;
    src: string;
    type: "image" | "video";
    poster?: string;
    webpSrc?: string;
    href?: string;
    comingSoon?: boolean;
};

const defaultFeatures: Feature[] = [
    {
        title: "Clothes tryon",
        desc: "Studio & lifestyle quality product shots with AI magic!",
        src: virtualTryon,
        type: "image",
        href: toolAppHref("Virtual Try-On"),
    },
    {
        title: "Fashion reels",
        desc: "AI-crafted videos that captivate your audience",
        src: FASHION_REELS_CDN,
        type: "video",
        poster: productShowcase,
        href: toolAppHref("AI Fashion reels"),
    },
    {
        title: "Catalogue generator",
        desc: "Products on AI models, no photoshoots needed",
        src: "https://cdn.pixro.ai/ab97372c-faca-4d0b-bb01-6bfdefb4ee23.jpg",
        type: "image",
        href: toolAppHref("Product showcase"),
    },
    {
        title: "Ghost mannequin",
        desc: "Invisible mannequin effects for market-ready visuals",
        src: ghostMannequin,
        type: "image",
        href: toolAppHref("Ghost mannequin"),
    },
    {
        title: "Flat lay",
        desc: "Perfectly styled flat lay shots in a click",
        src: flatLayPreview,
        type: "image",
        href: toolAppHref("Flat-lay"),
    },
    {
        title: "Upscale image",
        desc: "Boost resolution and clarity without losing detail",
        src: upscalePreview,
        type: "image",
        href: toolAppHref("Upscale"),
    },
];


interface FeaturesSectionProps {
    items?: Feature[];
    heading?: string;
    subheading?: string;
    featureHref?: string;
    /** Wrap the card row in the "Pixro.AI Studio" browser-chrome frame. Defaults to true. */
    showChrome?: boolean;
    /** Override the heading classes (e.g. to match a page's unified heading scale). */
    headingClassName?: string;
}

const FeaturesSection = ({
    items,
    heading,
    subheading,
    featureHref,
    showChrome = true,
    headingClassName = "text-3xl sm:text-4xl md:text-5xl text-[#0a0a0a] !leading-[1.1] font-extrabold",
}: FeaturesSectionProps = {}) => {
    const features = items ?? defaultFeatures;
    const scrollRef = useRef<HTMLDivElement>(null);
    const cardRowClassName = `flex items-stretch gap-4 overflow-x-auto overflow-y-hidden scrollbar-hide w-full ${
        showChrome ? "p-4 sm:p-6" : "py-2"
    }`;

    const scroll = (dir: "left" | "right") => {
        if (!scrollRef.current) return;
        const amount = 320;
        scrollRef.current.scrollBy({
            left: dir === "left" ? -amount : amount,
            behavior: "smooth",
        });
    };

    const cards = features.map((feature, idx) => {
        const effectiveHref = featureHref || feature.href;
        const clickable = !feature.comingSoon && !!effectiveHref;
        const isInternal = clickable && effectiveHref?.startsWith("/");
        const CardWrapper: React.ElementType = isInternal
            ? Link
            : clickable
              ? "a"
              : "div";
        const wrapperProps = isInternal
            ? { to: effectiveHref }
            : clickable
              ? {
                    href: effectiveHref,
                    target: "_blank",
                    rel: "noopener noreferrer",
                }
              : {};
        return (
            <Reveal
                key={feature.title}
                variant="fade-in"
                delay={Math.min(idx * 80, 480)}
                className="min-w-[260px] max-w-[260px] sm:min-w-[280px] sm:max-w-[280px] shrink-0"
            >
                <CardWrapper
                    {...wrapperProps}
                    aria-disabled={feature.comingSoon ? true : undefined}
                    className={`flex h-full flex-col w-full rounded-[18px] bg-[#050505] p-4 transition-transform ${
                        feature.comingSoon
                            ? "cursor-not-allowed"
                            : "hover:-translate-y-1"
                    }`}
                >
                    <div className="relative rounded-xl overflow-hidden aspect-[3/4] mb-4">
                        {feature.type === "video" ? (
                            <LazyVideo
                                src={feature.src}
                                poster={feature.poster}
                                controls
                                aria-hidden="true"
                                className="h-full w-full object-cover"
                            />
                        ) : (
                            <OptimizedImage
                                src={feature.src}
                                webpSrc={feature.webpSrc}
                                alt={feature.title}
                                loading="lazy"
                                width={768}
                                height={1024}
                                className="h-full w-full object-cover"
                            />
                        )}
                        {feature.comingSoon && (
                            <span className="absolute top-3 right-3 rounded-full bg-black/60 backdrop-blur px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
                                Coming soon
                            </span>
                        )}
                    </div>
                    <h3 className="font-extrabold text-white text-base">
                        {feature.title}
                    </h3>
                    <p className="mt-0.5 mb-3 text-xs text-white/60 leading-tight">
                        {feature.desc}
                    </p>
                    <span
                        className={`w-full rounded-lg py-2 text-xs font-medium transition-colors flex items-center justify-center gap-1.5 text-center mt-auto ${
                            feature.comingSoon
                                ? "bg-[#1f1f1f] text-white/50"
                                : "bg-[#292929] text-white hover:bg-[#333333]"
                        }`}
                    >
                        {feature.comingSoon ? (
                            "Coming soon"
                        ) : (
                            <>
                                Try now! <ArrowRight size={14} />
                            </>
                        )}
                    </span>
                </CardWrapper>
            </Reveal>
        );
    });

    const cardRow = (
        <div
            ref={scrollRef}
            className={cardRowClassName}
            style={{ scrollbarWidth: "none" }}
        >
            {cards}
        </div>
    );

    return (
        <section className="bg-[#75FB8D] py-16 overflow-hidden sm:py-20">
            {/* Header row - contained */}
            <div className="container mx-auto px-6">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-8">
                    <Reveal variant="fade-up" className="max-w-3xl">
                        <h2 className={headingClassName}>
                            {heading ??
                                "Your comprehensive platform for fashion-focused AI advertising."}
                        </h2>
                        <p className="mt-3 text-base text-[#0a0a0a]/70 max-w-xl">
                            {subheading ??
                                "Create stunning visuals, dynamic videos, and hyper-realistic models with AI, designed specifically for the fashion world."}
                        </p>
                    </Reveal>
                    <div className="flex gap-2 shrink-0 self-start sm:self-end">
                        <button
                            type="button"
                            aria-label="Previous feature"
                            onClick={() => scroll("left")}
                            className="w-10 h-10 rounded-xl bg-[#3f3f3f] text-white flex items-center justify-center hover:bg-[#333333] transition-colors"
                        >
                            <ArrowLeft size={18} />
                        </button>
                        <button
                            type="button"
                            aria-label="Next feature"
                            onClick={() => scroll("right")}
                            className="w-10 h-10 rounded-xl bg-[#3f3f3f] text-white flex items-center justify-center hover:bg-[#333333] transition-colors"
                        >
                            <ArrowRight size={18} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Card row, optionally framed in the browser-window chrome */}
            <div className="container mx-auto px-6">
                {showChrome ? (
                    <div className="rounded-2xl overflow-hidden bg-[#212226] border border-white/10">
                        {/* Window chrome */}
                        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
                            <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                            <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
                            <span className="w-3 h-3 rounded-full bg-[#28C840]" />
                            <span className="ml-3 text-xs font-medium text-white/60">
                                Pixro.AI Studio
                            </span>
                        </div>
                        <div className="flex">{cardRow}</div>
                    </div>
                ) : (
                    cardRow
                )}
            </div>
        </section>
    );
};

export default FeaturesSection;
